import { BadInputError } from '../errors/BadInputError';
import assertNever from '../utils/assertNever';
import { ASTExpr, stringifyExpr } from './ast';
import { evalExpression } from './eval';
import { ExprType, stringifyType } from './expr_types';
import { exprToSql } from './sql';
import { isSameType, typecheckExpression } from './typecheck';
import { Context, SqlExpression } from './types';
import { UnexpectedError } from '../errors/UnexpectedError';
import { l } from './template';
import {
  NoConversionError,
  Unit,
  findUnitConverterAst,
  isNotNullUnit,
  isNullUnit,
  isUnitCommensurable,
  isUnitEqual,
  unitToAllStrings,
  unitToString,
} from '../unit';
import { Quantity } from './quantity';
import { mapToCaseExprs, singleQuote } from '../sql';
import { mergeErrors, traceHelper } from './sql/internal';
import { TraceASTConversion, TraceASTType } from './trace';
import { RawSQL, dangerousRawSql, sql, sqlCaseF, sqlF } from '../sqlTemplate';
import { isNumberLiteral } from './literal';

export type TypeConversion = {
  // Call this function to get the runtime (aka JS eval) output of this
  // assignment.
  eval(): unknown;
  // Call this function to get the SQL expression (aka SQL eval) that computes
  // this assignment.
  toSql(): SqlExpression;
};

/**
 * Returns a `TypeConversion` with functions representing what you get when you
 * convert the specified expression to the specified type.
 */
export function convertToType(
  ctx: Context,
  {
    fromExpr,
    toType,
  }: {
    fromExpr: ASTExpr;
    toType: ExprType;
  }
): TypeConversion {
  const fromTy = typecheckExpression(ctx, fromExpr);
  return internalConvertToType(ctx, {
    fromExpr,
    fromType: fromTy,
    toType,
    originalFromType: fromTy,
  });
}

export const TypeConversionErrorReasons = {
  IncompatibleTypes: 'Incompatible types',
  IncompatibleUnits: 'Incompatible units',
} as const;
export type TypeConversionErrorReason = keyof typeof TypeConversionErrorReasons;

function unitListToString(units: ReadonlyArray<Unit>): string {
  return units.length === 0
    ? '<no unit>'
    : units.length === 1
      ? `"${unitToString(units[0])}"`
      : `[${units.map((u) => `"${unitToString(u)}"`).join(', ')}]`;
}

function stringifyTypeUnits(ty: ExprType): string {
  switch (ty.ty) {
    case 'nullable':
      return stringifyTypeUnits(ty.baseTy);
    case 'quantity':
      return unitListToString(ty.units);
    case 'number':
      return ty.unit ? unitListToString([ty.unit]) : unitListToString([]);
    default:
      // Since this is itself part of an error message, we produce a
      // human-readable value instead of stealing the show with our own error
      return `[ERROR: unexpected non-numeric type ${stringifyType(ty)}]`;
  }
}

export class TypeConversionError extends BadInputError {
  readonly name = 'TypeConversionError';
  readonly reason: TypeConversionErrorReason;

  constructor(
    {
      originalFromType,
      fromExpr,
      toType,
    }: {
      fromExpr: ASTExpr;
      originalFromType: ExprType;
      toType: ExprType;
    },
    reason: TypeConversionErrorReason,
    detail?: string
  ) {
    const parts: Array<string> = [];

    switch (reason) {
      case 'IncompatibleTypes':
        parts.push(
          `Could not convert value of type \`${stringifyType(
            originalFromType
          )}\` (from expression \`${stringifyExpr(
            fromExpr
          )}\`) to type \`${stringifyType(toType)}\``
        );
        break;
      case 'IncompatibleUnits':
        parts.push(
          `Unable to convert from ${stringifyTypeUnits(
            originalFromType
          )} to ${stringifyTypeUnits(toType)}`
        );
        break;
      default:
        assertNever(reason);
    }

    if (detail) {
      parts.push(detail);
    }
    super(parts.join(': '));

    this.reason = reason;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TypeConversionError);
    }
    Object.setPrototypeOf(this, TypeConversionError.prototype);
  }
}

function internalConvertToType(
  ctx: Context,
  {
    fromExpr,
    originalFromType,
    fromType,
    toType,
  }: {
    fromExpr: ASTExpr;
    originalFromType: ExprType;
    fromType: ExprType;
    toType: ExprType;
  }
): TypeConversion {
  const noop = (): TypeConversion => {
    return {
      eval: () => evalExpression(ctx, fromExpr),
      toSql: () => exprToSql(ctx, fromExpr),
    };
  };

  const err = (
    reason: TypeConversionErrorReason,
    detail?: string
  ): TypeConversionError => {
    return new TypeConversionError(
      {
        fromExpr,
        originalFromType,
        toType,
      },
      reason,
      detail
    );
  };

  switch (toType.ty) {
    // Assign to boolean.
    case 'boolean': {
      if (fromType.ty === 'boolean') {
        return noop();
      }
      throw err(
        'IncompatibleTypes',
        'only booleans can be assigned to the boolean type'
      );
    }

    // Assign to string.
    case 'string': {
      if (fromType.ty === 'string') {
        return noop();
      }
      throw err(
        'IncompatibleTypes',
        'only strings can be assigned to the string type'
      );
    }

    // Assign to null.
    case 'null': {
      if (fromType.ty === 'null') {
        return noop();
      }
      throw err(
        'IncompatibleTypes',
        'only null can be assigned to the null type'
      );
    }

    // Assign to date.
    case 'date': {
      switch (fromType.ty) {
        case 'date':
          return noop();
        case 'datetime':
          const convertedExpr = l`date(${fromExpr})`;
          return {
            eval: () => evalExpression(ctx, convertedExpr),
            toSql: () => exprToSql(ctx, convertedExpr),
          };
        default:
          throw err(
            'IncompatibleTypes',
            'only dates and datetimes can be assigned to the date type'
          );
      }
    }

    // Assign to datetime.
    case 'datetime': {
      switch (fromType.ty) {
        case 'date':
          const convertedExpr = l`datetime(${fromExpr})`;
          return {
            eval: () => evalExpression(ctx, convertedExpr),
            toSql: () => exprToSql(ctx, convertedExpr),
          };
        case 'datetime':
          return noop();
        default:
          throw err(
            'IncompatibleTypes',
            'only dates and datetimes can be assigned to the date type'
          );
      }
    }

    // Assign to list.
    case 'list': {
      if (
        fromType.ty === 'list' &&
        isSameType(toType.elemTy, fromType.elemTy)
      ) {
        return noop();
      }
      // In principle we could expand this to do unit conversions for numeric
      // values (i.e., List<kilogram> can be assigned to List<pound>), but
      // that's a pain in the ass and I don't know of a good use case
      throw err('IncompatibleTypes', 'list elements are not compatible');
    }

    // Assign to number.
    case 'number': {
      if (isSameType(fromType, toType)) {
        return noop();
      }

      // Special case: number literals (i.e., numbers that appear directly in
      // the source) can be assigned to any number, regardless of unit.
      if (isNumberLiteral(fromExpr)) {
        return noop();
      }

      switch (fromType.ty) {
        // Assign a number to a number.
        case 'number': {
          if (!isUnitAssignableTo(ctx, fromType.unit, toType.unit)) {
            throw err('IncompatibleUnits');
          }

          // Null can be assigned to null.
          if (isNullUnit(fromType.unit) && isNullUnit(toType.unit)) {
            return noop();
          }

          // One unit can be assigned to another if they are commensurable.
          // Because `isUnitAssignableTo` returned `true`, we know these units
          // are commensurable.

          const fromUnit = fromType.unit;
          const toUnit = toType.unit;

          if (isNullUnit(fromUnit) && isNotNullUnit(toUnit)) {
            if (ctx.useTypecheckStrictMode) {
              // this should not happen!
              throw err('IncompatibleUnits');
            }
            const converter = createExprUnitConverter(fromUnit ?? null, toUnit);
            const convertedExpr = converter(fromExpr);
            return {
              eval: () => evalExpression(ctx, convertedExpr),
              toSql: () => exprToSql(ctx, convertedExpr),
            };
          }

          if (isNotNullUnit(fromUnit) && isNotNullUnit(toUnit)) {
            const converter = createExprUnitConverter(fromUnit ?? null, toUnit);
            const convertedExpr = converter(fromExpr);
            return {
              eval: () => evalExpression(ctx, convertedExpr),
              toSql() {
                const fromSql = exprToSql(ctx, fromExpr);
                const convertedSql = exprToSql(ctx, convertedExpr);
                return traceHelper(
                  { ty: 'number', unit: toUnit },
                  {
                    expr: `(${convertedSql.expr})`,
                    stage: fromSql.stage,
                    unitExpr: null,
                    error: fromSql.error,
                  },
                  (sub) => ({
                    t: TraceASTType.TConversion,
                    e: sub(fromSql.trace),
                    d: sqlTraceDescription(fromUnit ?? null, toUnit),
                  })
                );
              },
            };
          }

          // This indicates drift between the definition of `isUnitAssignableTo`
          // and the code here.
          const fromName = fromType.unit
            ? unitToString(fromType.unit)
            : '<no unit>';
          const toName = toType.unit ? unitToString(toType.unit) : '<no unit>';
          throw new UnexpectedError(
            `Unexpected case in conversion from "${fromName}" to "${toName}"`
          );
        }

        // Assign a quantity to a number.
        case 'quantity': {
          const toUnit = toType.unit;

          if (
            !fromType.units.some((fromUnit) =>
              isUnitAssignableTo(ctx, fromUnit, toUnit)
            )
          ) {
            throw err('IncompatibleUnits');
          }

          UnexpectedError.invariant(
            toUnit,
            'must have unit (per isUnitAssignableTo)'
          );

          // Maps from one of the `from` units to the function that converts it
          // to a `to` unit.
          const converters = new Map(
            fromType.units.flatMap((unit) =>
              unitToAllStrings(unit).map((unitStr: string) => [
                unitStr,
                createExprUnitConverter(unit, toUnit),
              ])
            )
          );
          return {
            eval() {
              const v = evalExpression(ctx, fromExpr);
              if (!(v instanceof Quantity)) {
                throw new UnexpectedError(
                  `expression "${stringifyExpr(
                    fromExpr
                  )}" typechecked as type "${stringifyType(
                    fromType
                  )}", but had runtime type "${typeof v}" (${JSON.stringify(
                    v
                  )})`
                );
              }
              const convert = converters.get(unitToString(v.unit));
              if (convert === undefined) {
                throw new UnexpectedError(
                  `expression "${stringifyExpr(
                    fromExpr
                  )}" typechecked as type "${stringifyType(
                    fromType
                  )}", but Quantity had runtime unit "${unitToString(v.unit)}"`
                );
              }
              return evalExpression(ctx, convert(l`${v.value}`));
            },
            toSql() {
              const fromSql = exprToSql(ctx, fromExpr);
              const fromUnitExpr = fromSql.unitExpr;
              if (fromUnitExpr == null) {
                throw new UnexpectedError(
                  `expression "${stringifyExpr(
                    fromExpr
                  )}" typechecked as type "${stringifyType(
                    fromType
                  )}", but did not have a unitExpr`
                );
              }

              const unitFactors = new Map<string, string>();
              const traceDescriptions = new Map<string, RawSQL>();
              const validUnits: Array<Unit> = [];
              const failingUnits: Array<Unit> = [];
              for (const eUnit of fromType.units) {
                let converter: ((v: ASTExpr) => ASTExpr) | undefined;
                try {
                  converter = findUnitConverterAst(eUnit, toUnit);
                  validUnits.push(eUnit);
                } catch (e) {
                  if (e instanceof NoConversionError) {
                    failingUnits.push(eUnit);
                  } else {
                    throw e;
                  }
                }
                if (!converter) {
                  continue;
                }

                const convertedExpr = converter({
                  t: 'call',
                  name: 'cast',
                  args: [fromExpr, { t: 'string', v: unitToString(eUnit) }],
                  kwargs: new Map(),
                });
                const convertedSql = exprToSql(ctx, convertedExpr);
                const traceDescription = sqlTraceDescription(eUnit, toUnit);
                const stringUnit = unitToString(eUnit);
                unitFactors.set(stringUnit, convertedSql.expr);
                traceDescriptions.set(stringUnit, traceDescription);
              }

              const factorExpr = mapToCaseExprs(
                unitFactors,
                fromUnitExpr,
                undefined
              );

              // We want a runtime exception if a given unit is not in the list of allowed
              // units.
              const allowedUnitsText = `'${validUnits
                .map(unitToString)
                .join("', '")}'`;
              const errorExpression = sql`'Cannot convert from ' || ${dangerousRawSql(
                fromUnitExpr
              )} || ' to ' || ${unitToString(
                toUnit
              )} || '. Supported unit(s): ' || ${allowedUnitsText}`;
              const sqlError = {
                exists: `${fromUnitExpr} NOT IN (${allowedUnitsText})`,
                expr: errorExpression,
              };

              return traceHelper<TraceASTConversion>(
                { ty: 'number', unit: toUnit },
                {
                  expr: `(${factorExpr})`,
                  stage: fromSql.stage,
                  unitExpr: null,
                  error: mergeErrors([fromSql.error, sqlError]),
                },
                (sub) => ({
                  t: TraceASTType.TConversion,
                  e: sub(fromSql.trace),
                  d: sqlCaseF(traceDescriptions, dangerousRawSql(fromUnitExpr)),
                })
              );
            },
          };
        }

        default:
          throw err(
            'IncompatibleTypes',
            'only numbers and quantities can be assigned to the number type'
          );
      }
    }

    // Assign to quantity:
    case 'quantity': {
      switch (fromType.ty) {
        // Assign number to quantity.
        case 'number': {
          const fromUnit = fromType.unit;

          if (fromUnit === undefined) {
            // Maybe possible in theory? But the null unit isn't very helpful as
            // a member of a quantity, so I can't see a use case to implement
            // this.
            throw err(
              'IncompatibleTypes',
              'cannot convert from null unit to quantity'
            );
          }

          let candidate: Unit | undefined = undefined;
          // Prefer an exact match, so first look for that.
          for (const unit of toType.units) {
            if (isUnitEqual(unit, fromUnit)) {
              candidate = unit;
              break;
            }
          }
          if (candidate === undefined) {
            // Now get the first commensurable match.
            for (const unit of toType.units) {
              if (isUnitAssignableTo(ctx, unit, fromUnit)) {
                candidate = unit;
                break;
              }
            }
          }
          if (candidate === undefined) {
            // Now give up.
            throw err('IncompatibleUnits');
          }
          const toUnit = candidate;

          const converter = createExprUnitConverter(fromUnit, toUnit);
          const convertedExpr = converter(fromExpr);
          return {
            eval() {
              const v = evalExpression(ctx, convertedExpr);
              if (typeof v !== 'number') {
                throw new UnexpectedError(
                  `expression \`${stringifyExpr(
                    fromExpr
                  )}\` typechecked as type \`${stringifyType(
                    fromType
                  )}\`, but had runtime type \`${typeof v}\` (${JSON.stringify(
                    v
                  )}), not number`
                );
              }
              return new Quantity(v, toUnit);
            },
            toSql() {
              const fromSql = exprToSql(ctx, fromExpr);
              const convertedSql = exprToSql(ctx, convertedExpr);
              return traceHelper<TraceASTConversion>(
                { ty: 'number', unit: toUnit },
                {
                  expr: convertedSql.expr,
                  stage: fromSql.stage,
                  unitExpr: singleQuote(unitToString(toUnit)),
                  error: fromSql.error,
                },
                (sub) => ({
                  t: TraceASTType.TConversion,
                  e: sub(fromSql.trace),
                  d: sqlTraceDescription(fromUnit ?? null, toUnit),
                })
              );
            },
          };
        }

        // Assign a quantity to a quantity.
        case 'quantity': {
          if (isSameType(fromType, toType)) {
            return noop();
          }
          // TODO(carl): In principle there's no reason we couldn't support
          // this! But I'm lazy and don't know of any good use cases.
          //
          // If you're from The Future and want to support this, mechanically
          // the thing to do is to convert every unit option in the "source"
          // quantity to one of the options in the "destination" quantity, but
          // to prefer keeping the value and unit if both quantities support a
          // given unit.
          throw err(
            'IncompatibleTypes',
            'quantities cannot be assigned to other quantities (but talk to us if you have a good use case)'
          );
        }

        default:
          throw err(
            'IncompatibleTypes',
            'only numbers and quantities can be assigned to the quantity type'
          );
      }
    }

    // Assign to nullable.
    case 'nullable': {
      switch (fromType.ty) {
        case 'nullable':
          return internalConvertToType(ctx, {
            originalFromType,
            fromType: fromType.baseTy,
            toType: toType.baseTy,
            fromExpr,
          });
        case 'null':
          return noop();
        default:
          return internalConvertToType(ctx, {
            originalFromType,
            fromType,
            toType: toType.baseTy,
            fromExpr,
          });
      }
    }

    default:
      assertNever(toType);
  }
}

// Returns true if a value of unit `from` can be assigned to a variable with
// unit `to`, and false otherwise. This assignment is possible if:
// - Both units are defined, and they are commensurable.
// - Both units are null.
// We're allowing the following case at runtime for the time being, but plan
// to eliminate instances of this:
// - `from` is null, so we can just cast it to the `to` unit.
export function isUnitAssignableTo(
  ctx: Context,
  from: Unit | null | undefined,
  to: Unit | null | undefined
): boolean {
  if (isNotNullUnit(to)) {
    if (isNotNullUnit(from)) {
      return isUnitCommensurable(to, from);
    } else {
      return !ctx.useTypecheckStrictMode;
    }
  }
  return isNullUnit(from);
}

function createExprUnitConverter(
  from: Unit | null,
  to: Unit
): (expr: ASTExpr) => ASTExpr {
  return (expr) => {
    if (!from) {
      return l`cast(${expr}, ${unitToString(to)})`;
    }
    // Need to cast the expression in case it's a quantity
    const castToFrom = l`cast(${expr}, ${unitToString(from)})`;
    const converter = findUnitConverterAst(from, to);
    const converted = converter(castToFrom);
    // Cast to resulting unit to make downstream typechecking work
    return l`cast(${converted}, ${unitToString(to)})`;
  };
}

function sqlTraceDescription(from: Unit, to: Unit): RawSQL {
  const fromStr = unitToString(from);
  const toStr = unitToString(to);
  // This is a bit of an abuse of LScript syntax and the way expression
  // stringification happens to work, but the goal is to produce a description
  // expression that looks roughly like this:
  //
  // [kilogram-kilometer] = 0.453592 * (1.60934 * [pound-mile])
  //
  // We se `findUnitConverter` directly here, instead of the output of
  // `createExprUnitConverter`, to avoid `cast()` calls in the string.
  const converter = findUnitConverterAst(from, to);
  const traceEquation = converter({ t: 'identifier', name: `[${fromStr}]` });
  return sqlF`'[${toStr}] = ${stringifyExpr(traceEquation)}'`;
}
